'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface HaalSnapshot {
  id: string;
  zahir: string;
  batin: string;
  name_of_allah: string;
  ayat_ref: string;
  vantage_point: string;
  location?: string;
  created_at: string;
  is_offline?: boolean;
}

const VANTAGE_POINTS = [
  { id: 'ecological', label: 'Ecological', icon: '🌿', color: '#2d7a47' },
  { id: 'justice', label: 'Justice', icon: '⚖️', color: '#c9a84c' },
  { id: 'inner-seeker', label: 'Inner Seeker', icon: '◎', color: '#8a8ab8' },
  { id: 'community', label: 'Community', icon: '⬡', color: '#2d8a87' },
  { id: 'academic', label: 'Academic', icon: '✦', color: '#D4A82A' },
];

const NAMES_QUICK = [
  'Al-Qadir', 'Al-Khaliq', 'Al-Bari', 'Al-Musawwir', 'Ar-Razzaq', 'An-Nur',
  'Al-Latif', 'Al-Alim', 'Az-Zahir', 'Al-Batin', 'Al-Hadi', 'Al-Wadud',
];

const OFFLINE_KEY = 'haal_offline_snapshots';

// ─── SNAPSHOT CARD ────────────────────────────────────────────────────────────

function SnapshotCard({ snapshot }: { snapshot: HaalSnapshot }) {
  const vp = VANTAGE_POINTS.find(v => v.id === snapshot.vantage_point) || VANTAGE_POINTS[0];
  const date = new Date(snapshot.created_at);

  return (
    <div style={{
      background: `${vp.color}06`,
      border: `1px solid ${vp.color}20`,
      borderRadius: 8, padding: '12px 14px',
      position: 'relative',
    }}>
      {snapshot.is_offline && (
        <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 7, fontFamily: 'monospace', color: '#c9a84c', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 3, padding: '1px 5px' }}>
          OFFLINE
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10 }}>{vp.icon}</span>
        <span style={{ fontSize: 8, color: vp.color, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase' }}>{vp.label}</span>
        {snapshot.name_of_allah && <span style={{ fontSize: 8, color: '#c9a84c', fontFamily: 'monospace' }}>· {snapshot.name_of_allah}</span>}
        {snapshot.ayat_ref && <span style={{ fontSize: 8, color: '#2d8a87', fontFamily: 'monospace' }}>· {snapshot.ayat_ref}</span>}
        <span style={{ fontSize: 7, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace', marginLeft: 'auto' }}>
          {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.5, marginBottom: snapshot.batin ? 6 : 0 }}>
        <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.08em', marginRight: 5 }}>Zahir</span>
        {snapshot.zahir}
      </p>
      {snapshot.batin && (
        <p style={{ fontSize: 11, color: 'rgba(138,138,184,0.7)', lineHeight: 1.5, fontStyle: 'italic' }}>
          <span style={{ fontSize: 8, color: 'rgba(138,138,184,0.4)', textTransform: 'uppercase', letterSpacing: '.08em', marginRight: 5, fontStyle: 'normal' }}>Batin</span>
          {snapshot.batin}
        </p>
      )}
      {snapshot.location && (
        <p style={{ fontSize: 9, color: '#2d7a47', fontFamily: 'monospace', marginTop: 4 }}>📍 {snapshot.location}</p>
      )}
    </div>
  );
}

// ─── MAIN HAAL SNAPSHOT ───────────────────────────────────────────────────────

export default function HaalSnapshot() {
  const [snapshots, setSnapshots] = useState<HaalSnapshot[]>([]);
  const [showCapture, setShowCapture] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Quick capture state
  const [zahir, setZahir] = useState('');
  const [batin, setBatin] = useState('');
  const [nameOfAllah, setNameOfAllah] = useState('');
  const [ayatRef, setAyatRef] = useState('');
  const [vantagePoint, setVantagePoint] = useState('ecological');
  const [location, setLocation] = useState('');

  const supabase = createClient();

  useEffect(() => {
    setIsOnline(navigator.onLine);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  // Load snapshots
  useEffect(() => {
    async function load() {
      // Load offline snapshots
      const offline: HaalSnapshot[] = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]');

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('irl_observations')
            .select('*')
            .eq('user_id', user.id)
            .is('chain_id', null)
            .order('created_at', { ascending: false })
            .limit(10);

          if (data) {
            const online: HaalSnapshot[] = data.map((d: any) => ({
              id: d.id,
              zahir: d.zahir_text || d.observation_text || '',
              batin: d.batin_text || '',
              name_of_allah: d.name_of_allah || '',
              ayat_ref: d.ayat_ref || '',
              vantage_point: d.vantage_point || 'ecological',
              location: d.location || '',
              created_at: d.created_at,
            }));
            setSnapshots([...offline, ...online]);
            return;
          }
        }
      } catch { /* silent */ }
      setSnapshots(offline);
    }
    load();
  }, [supabase]);

  // Sync offline snapshots when online
  useEffect(() => {
    if (!isOnline) return;
    async function syncOffline() {
      const offline: HaalSnapshot[] = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]');
      if (offline.length === 0) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        for (const snap of offline) {
          await supabase.from('irl_observations').insert({
            user_id: user.id,
            zahir_text: snap.zahir,
            batin_text: snap.batin,
            name_of_allah: snap.name_of_allah,
            ayat_ref: snap.ayat_ref,
            vantage_point: snap.vantage_point,
            location: snap.location,
          });
        }
        localStorage.removeItem(OFFLINE_KEY);
      } catch { /* silent */ }
    }
    syncOffline();
  }, [isOnline, supabase]);

  async function captureSnapshot() {
    if (!zahir.trim()) return;
    setCapturing(true);

    const snapshot: HaalSnapshot = {
      id: `snap-${Date.now()}`,
      zahir: zahir.trim(),
      batin: batin.trim(),
      name_of_allah: nameOfAllah,
      ayat_ref: ayatRef.trim(),
      vantage_point: vantagePoint,
      location: location.trim(),
      created_at: new Date().toISOString(),
      is_offline: !isOnline,
    };

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && isOnline) {
        await supabase.from('irl_observations').insert({
          user_id: user.id,
          zahir_text: snapshot.zahir,
          batin_text: snapshot.batin,
          name_of_allah: snapshot.name_of_allah,
          ayat_ref: snapshot.ayat_ref,
          vantage_point: snapshot.vantage_point,
          location: snapshot.location,
        });
        snapshot.is_offline = false;
      } else {
        // Queue offline
        const queue: HaalSnapshot[] = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]');
        queue.unshift(snapshot);
        localStorage.setItem(OFFLINE_KEY, JSON.stringify(queue));
      }
    } catch {
      const queue: HaalSnapshot[] = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]');
      queue.unshift(snapshot);
      localStorage.setItem(OFFLINE_KEY, JSON.stringify(queue));
      snapshot.is_offline = true;
    }

    setSnapshots(prev => [snapshot, ...prev]);
    setZahir(''); setBatin(''); setNameOfAllah(''); setAyatRef(''); setLocation('');
    setShowCapture(false);
    setCapturing(false);
  }

  const vp = VANTAGE_POINTS.find(v => v.id === vantagePoint) || VANTAGE_POINTS[0];
  const offlineCount = snapshots.filter(s => s.is_offline).length;

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <p style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 2 }}>
            ◎ Haal Snapshot
          </p>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)' }}>
            Capture the full state of a moment in one tap
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isOnline && (
            <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#c9a84c', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 3, padding: '2px 6px' }}>
              OFFLINE
            </span>
          )}
          <button
            onClick={() => setShowCapture(v => !v)}
            style={{
              fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
              background: showCapture ? `${vp.color}15` : 'rgba(201,168,76,0.08)',
              border: `1px solid ${showCapture ? vp.color + '40' : 'rgba(201,168,76,0.25)'}`,
              color: showCapture ? vp.color : '#c9a84c',
            }}
          >
            {showCapture ? '✕ Cancel' : '◎ Capture Haal'}
          </button>
        </div>
      </div>

      {/* Offline sync notice */}
      {offlineCount > 0 && isOnline && (
        <div style={{ marginBottom: 10, padding: '6px 12px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 5 }}>
          <p style={{ fontSize: 9, color: '#2d7a47', fontFamily: 'monospace' }}>
            ✓ {offlineCount} offline snapshot{offlineCount !== 1 ? 's' : ''} syncing now...
          </p>
        </div>
      )}

      {/* Capture form */}
      {showCapture && (
        <div style={{ background: 'rgba(10,25,41,0.7)', border: `1px solid ${vp.color}25`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 9, color: '#c9a84c', fontFamily: 'monospace', letterSpacing: '.12em', textTransform: 'uppercase' }}>
              Haal Snapshot — {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            {!isOnline && <span style={{ fontSize: 8, color: '#c9a84c', fontFamily: 'monospace' }}>· Will sync when online</span>}
          </div>

          {/* Vantage point — quick select */}
          <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
            {VANTAGE_POINTS.map(v => (
              <button key={v.id} onClick={() => setVantagePoint(v.id)} style={{ fontSize: 8, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 20, cursor: 'pointer', background: vantagePoint === v.id ? `${v.color}18` : 'rgba(255,255,255,0.02)', border: `1px solid ${vantagePoint === v.id ? v.color + '50' : 'rgba(255,255,255,0.06)'}`, color: vantagePoint === v.id ? v.color : 'rgba(212,197,169,0.35)', transition: 'all 0.12s' }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>

          {/* Zahir */}
          <textarea value={zahir} onChange={e => setZahir(e.target.value)} placeholder="Zahir — What do you observe right now? (required)" rows={2} style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, marginBottom: 8 }} />

          {/* Batin */}
          <textarea value={batin} onChange={e => setBatin(e.target.value)} placeholder="Batin — What deeper reality do you sense? (optional)" rows={2} style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, marginBottom: 8 }} />

          {/* Name + Ayat + Location row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <select value={nameOfAllah} onChange={e => setNameOfAllah(e.target.value)} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '6px 8px', color: nameOfAllah ? '#F5F0E8' : 'rgba(212,197,169,0.3)', fontSize: 10, outline: 'none', fontFamily: 'inherit' }}>
              <option value="">Name of Allah...</option>
              {NAMES_QUICK.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <input type="text" value={ayatRef} onChange={e => setAyatRef(e.target.value)} placeholder="Ayat (e.g. 41:53)" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '6px 8px', color: '#F5F0E8', fontSize: 10, outline: 'none', fontFamily: 'inherit' }} />
            <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Location (optional)" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '6px 8px', color: '#F5F0E8', fontSize: 10, outline: 'none', fontFamily: 'inherit' }} />
          </div>

          {/* One-tap capture button */}
          <button
            onClick={captureSnapshot}
            disabled={!zahir.trim() || capturing}
            style={{
              width: '100%', padding: '12px', borderRadius: 8, cursor: zahir.trim() ? 'pointer' : 'not-allowed',
              background: zahir.trim() ? `${vp.color}15` : 'rgba(255,255,255,0.03)',
              border: `2px solid ${zahir.trim() ? vp.color + '50' : 'rgba(255,255,255,0.06)'}`,
              color: zahir.trim() ? vp.color : 'rgba(212,197,169,0.3)',
              fontSize: 11, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
              transition: 'all 0.2s',
            }}
          >
            {capturing ? 'Capturing...' : `◎ Capture Haal Snapshot${!isOnline ? ' (Offline)' : ''}`}
          </button>
        </div>
      )}

      {/* Recent snapshots */}
      {snapshots.length > 0 && (
        <div>
          <p style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Recent Snapshots ({snapshots.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {snapshots.slice(0, 5).map(snap => (
              <SnapshotCard key={snap.id} snapshot={snap} />
            ))}
          </div>
        </div>
      )}

      {snapshots.length === 0 && !showCapture && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'rgba(212,197,169,0.25)', fontSize: 11 }}>
          No snapshots yet — capture your first Haal moment
        </div>
      )}
    </div>
  );
}
